import { Flex, Stack, useMantineTheme } from '@mantine/core';
import debounce from 'debounce-promise';
import { when } from 'mobx';
import type { JSX } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ISelectOption } from '@feathr/components';
import {
  Alert,
  NumberEntry,
  Skeleton,
  Text,
  UniversalSelect,
  Well,
  zIndex,
} from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT } from '@feathr/hooks';

import type { IMediaBalanceFormErrors, IProjectMediaBalance } from '../RedeemCreditsModal.utils';
import { formatCredits, formatCurrency } from '../RedeemCreditsModal.utils';

import * as styles from '../RedemptionTypeSelect/RedemptionTypeSelect.css';

// A small page keeps the initial dropdown fast and makes infinite-scroll load-more fire quickly,
// rather than only after a large first page.
const PROJECTS_PAGE_SIZE = 25;

export interface IMediaBalanceFormProps {
  availableCredits: number;
  convertedAmount: number;
  creditsToRedeem: number | undefined;
  isCalculating?: boolean;
  isLoadingProject?: boolean;
  onCreditsChange: (credits: number | undefined) => void;
  onProjectChange: (projectId: string) => void;
  projectBalance?: IProjectMediaBalance;
  projectName: string | undefined;
  selectedProjectId: string | undefined;
  validationErrors: IMediaBalanceFormErrors;
}

/**
 * Step 2-3: Form for selecting a project and entering credits amount.
 * Displays conversion preview and project media balance information.
 */
function MediaBalanceForm({
  availableCredits,
  convertedAmount,
  creditsToRedeem,
  isCalculating,
  isLoadingProject,
  onCreditsChange,
  onProjectChange,
  projectBalance,
  projectName,
  selectedProjectId,
  validationErrors,
}: Readonly<IMediaBalanceFormProps>): JSX.Element {
  const { t } = useTranslation();
  const { Events: Projects } = useStore();
  const {
    spacing: { '2': innerGap, '4': sectionGap },
  } = useMantineTheme();
  // Remember the picked option so the select keeps showing it while the parent reloads the
  // project (projectName is set only after that async fetch), avoiding a placeholder flash.
  const [selectedOption, setSelectedOption] = useState<ISelectOption>();

  const loadProjects = useCallback(
    async (
      inputValue: string,
      _loaded: readonly ISelectOption[],
      additional?: { page: number },
    ): Promise<{ additional: { page: number }; hasMore: boolean; options: ISelectOption[] }> => {
      const page = additional?.page ?? 0;
      const projects = Projects.list({
        filters: {
          is_archived__ne: true,
          ...(inputValue ? { name__icontains: inputValue } : {}),
        },
        only: ['id', 'name'],
        ordering: ['name'],
        pagination: { page, page_size: PROJECTS_PAGE_SIZE },
      });
      await when(() => !projects.isPending);
      const options = projects.models.map(({ id, name }) => ({ id, name }));
      return {
        additional: { page: page + 1 },
        hasMore: options.length === PROJECTS_PAGE_SIZE,
        options,
      };
    },
    [Projects],
  );

  const debouncedLoadProjects = useMemo(
    () => debounce(loadProjects, DEFAULT_DEBOUNCE_WAIT),
    [loadProjects],
  );

  function handleProjectSelect(option: ISelectOption): void {
    setSelectedOption(option);
    onProjectChange(option.id);
  }

  function handleCreditsChange(value: string | number): void {
    if (typeof value === 'number') {
      onCreditsChange(value);
      return;
    }
    const numValue = value === '' ? undefined : Number(value);
    onCreditsChange(numValue);
  }

  const isFormComplete =
    selectedProjectId &&
    creditsToRedeem !== undefined &&
    creditsToRedeem > 0 &&
    !validationErrors.credits &&
    !validationErrors.project;

  return (
    <Stack gap={sectionGap}>
      <Well className={styles.availableCredits} theme={'blue'}>
        <Text.DefaultHeadingMedium className={styles.availableCreditsText}>
          {t('Available Credits')}
        </Text.DefaultHeadingMedium>
        <Text.DefaultHeadingMedium className={styles.availableCreditsText}>
          {availableCredits.toLocaleString()}
        </Text.DefaultHeadingMedium>
      </Well>

      <UniversalSelect<ISelectOption>
        cacheOptions={true}
        defaultOptions={true}
        getOptionLabel={({ name }: ISelectOption): string => name}
        getOptionValue={({ id }: ISelectOption): string => id}
        label={t('Project')}
        loadPagedOptions={debouncedLoadProjects}
        menuPortalTarget={document.body}
        name={'project-select'}
        onSelectSingle={handleProjectSelect}
        paginate={true}
        placeholder={t('Select a project')}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: zIndex.popover }) }}
        value={
          selectedProjectId
            ? { id: selectedProjectId, name: projectName ?? selectedOption?.name ?? '' }
            : undefined
        }
      />

      {selectedProjectId && (isLoadingProject || projectBalance) && (
        <Well theme={'neutral'}>
          <Stack gap={innerGap} w={'100%'}>
            <Flex align={'center'} justify={'space-between'} w={'100%'}>
              <Text.DefaultHeadingMedium>{t('Current Media Balance')}</Text.DefaultHeadingMedium>
              <Text.DefaultRegular>
                {isLoadingProject ? (
                  <Skeleton width={60} />
                ) : (
                  formatCurrency(projectBalance?.currentBalance ?? 0)
                )}
              </Text.DefaultRegular>
            </Flex>
            <Flex align={'center'} justify={'space-between'} w={'100%'}>
              <Text.DefaultHeadingMedium>
                {t('Spent in {{monthYear}}', {
                  monthYear: new Date().toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric',
                  }),
                })}
              </Text.DefaultHeadingMedium>
              <Text.DefaultRegular>
                {isLoadingProject ? (
                  <Skeleton width={60} />
                ) : (
                  formatCurrency(projectBalance?.spent ?? 0)
                )}
              </Text.DefaultRegular>
            </Flex>
          </Stack>
        </Well>
      )}

      <NumberEntry
        description={t('Enter the number of credits you want to convert to media balance.')}
        error={creditsToRedeem !== undefined ? validationErrors.credits : undefined}
        label={t('Credits to Redeem')}
        max={availableCredits}
        min={1}
        name={'credits-to-redeem'}
        onChange={handleCreditsChange}
        value={creditsToRedeem ?? 0}
      />

      {isFormComplete && (
        <Alert
          description={
            isCalculating ? (
              t('Calculating conversion...')
            ) : (
              <>
                {t('Redeeming')} <strong>{formatCredits(creditsToRedeem)}</strong> {t('credits')} ={' '}
                <strong>{formatCurrency(convertedAmount)}</strong> {t('added to')}{' '}
                <strong>{projectName ?? t('selected project')}</strong> {t('media balance')}
              </>
            )
          }
          title={t('Conversion summary')}
          type={'info'}
        />
      )}
    </Stack>
  );
}

export default MediaBalanceForm;
